import { Button } from "@/components/design-system/button";
import Modal from "@/components/design-system/Modal";
import {
  getSchoolCourseTermAccessService,
  updateSchoolCourseTermAccessService,
} from "@/service/school.service";
import {
  SchoolCourseTermAccessCourse,
  SchoolCourseTermAccessTerm,
} from "@/types/school.model";
import { successToast } from "@/utils/constants";
import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

interface CourseTermAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  schoolId: string | null;
}

function sortTermNames(names: string[]): string[] {
  return [...names].sort((a, b) => {
    const numA = parseInt(a.replace(/\D/g, ""), 10) || 0;
    const numB = parseInt(b.replace(/\D/g, ""), 10) || 0;
    if (numA !== numB) return numA - numB;
    return a.localeCompare(b);
  });
}

function collectTermColumns(courses: SchoolCourseTermAccessCourse[]): string[] {
  const names = new Set<string>();
  for (const course of courses) {
    for (const term of course.terms ?? []) {
      if (term.name) names.add(term.name);
    }
  }
  return sortTermNames([...names]);
}

const CHECKBOX_BLUE = "#5C88C5";
const CHECKBOX_UNCHECKED_BORDER = "#354052";

function TermAccessCheckbox({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="mx-auto flex h-[20px] w-[20px] shrink-0 items-center justify-center rounded-[4px] border-2 bg-white transition-colors"
      style={{
        borderColor: checked ? CHECKBOX_BLUE : CHECKBOX_UNCHECKED_BORDER,
      }}
    >
      {checked ? (
        <svg
          width="12"
          height="9"
          viewBox="0 0 12 9"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden
        >
          <path
            d="M1 4.5L4 7.5L11 1"
            stroke={CHECKBOX_BLUE}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : null}
    </button>
  );
}

export default function CourseTermAccessModal({
  isOpen,
  onClose,
  schoolId,
}: CourseTermAccessModalProps) {
  const [schoolName, setSchoolName] = useState("");
  const [courses, setCourses] = useState<SchoolCourseTermAccessCourse[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const termColumns = useMemo(() => collectTermColumns(courses), [courses]);

  const fetchAccess = useCallback(async () => {
    if (!schoolId) return;
    setLoading(true);
    try {
      const response = await getSchoolCourseTermAccessService(schoolId);
      const data = response.data.data;
      setSchoolName(data.school_name ?? "");
      setCourses(
        (data.courses ?? []).map((course) => ({
          ...course,
          terms: (course.terms ?? []).map((term) => ({ ...term })),
        }))
      );
    } catch {
      // errorToast("Failed to load courses", "Could not fetch course term access.");
      onClose();
    } finally {
      setLoading(false);
    }
  }, [schoolId, onClose]);

  useEffect(() => {
    if (isOpen && schoolId) {
      fetchAccess();
    }
    if (!isOpen) {
      setSchoolName("");
      setCourses([]);
    }
  }, [isOpen, schoolId, fetchAccess]);

  const handleTermToggle = (
    courseIndex: number,
    termName: string,
    checked: boolean
  ) => {
    setCourses((prev) =>
      prev.map((course, idx) => {
        if (idx !== courseIndex) return course;
        const terms = [...(course.terms ?? [])];
        const termIndex = terms.findIndex((t) => t.name === termName);
        if (termIndex >= 0) {
          terms[termIndex] = { ...terms[termIndex], is_allowed: checked };
          return { ...course, terms };
        }
        return course;
      })
    );
  };

  const getTermForColumn = (
    course: SchoolCourseTermAccessCourse,
    termName: string
  ): SchoolCourseTermAccessTerm | undefined =>
    course.terms?.find((t) => t.name === termName);

  const handleUpdate = async () => {
    if (!schoolId) return;
    setSaving(true);
    try {
      await updateSchoolCourseTermAccessService(schoolId, {
        courses: courses.map((course) => ({
          course_id: course.course_id,
          terms: (course.terms ?? []).map((term) => ({
            term_id: term.term_id,
            is_allowed: term.is_allowed,
          })),
        })),
      });
      successToast("Updated successfully", "Course term access has been saved.");
      onClose();
    } catch {
      console.error("Could not save course term access.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Courses"
      subtitle={schoolName}
      titleClassName="text-[24px] font-semibold text-[#1F1F1F]"
      subtitleClassName="mt-1 text-[14px] font-semibold text-[#1F1F1F]"
      width="900px"
      contentClassName="p-0"
    >
      <div className="max-h-[420px] overflow-auto border-t border-[#EDEDED]">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-[#5C88C5]" />
          </div>
        ) : courses.length === 0 ? (
          <p className="px-6 py-10 text-center text-[16px] text-[#7B7B7B]">
            No courses found for this school.
          </p>
        ) : (
          <table className="w-full border-collapse">
            <thead className="sticky top-0 z-10 bg-[#F9F9F9]">
              <tr>
                <th className="w-[72px] border-b border-[#EDEDED] px-4 py-4 text-left text-[16px] font-semibold text-[#1F1F1F]">
                  S.No
                </th>
                <th className="min-w-[200px] border-b border-[#EDEDED] px-4 py-4 text-left text-[16px] font-semibold text-[#1F1F1F]">
                  Course Name
                </th>
                {termColumns.map((termName) => (
                  <th
                    key={termName}
                    className="min-w-[100px] border-b border-[#EDEDED] px-4 py-4 text-center text-[16px] font-semibold text-[#1F1F1F]"
                  >
                    {termName}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {courses.map((course, index) => (
                <tr
                  key={course.course_id}
                  className={index % 2 === 0 ? "bg-white" : "bg-[#F9F9F9]"}
                >
                  <td className="border-b border-[#EDEDED] px-4 py-5 text-[16px] font-medium text-[#1F1F1F]">
                    {String(index + 1).padStart(2, "0")}
                  </td>
                  <td className="border-b border-[#EDEDED] px-4 py-5 text-[16px] font-medium text-[#1F1F1F]">
                    {course.grade}
                  </td>
                  {termColumns.map((termName) => {
                    const term = getTermForColumn(course, termName);
                    if (!term) {
                      return (
                        <td
                          key={`${course.course_id}-${termName}`}
                          className="border-b border-[#EDEDED] px-4 py-5"
                        />
                      );
                    }
                    return (
                      <td
                        key={`${course.course_id}-${term.term_id}`}
                        className="border-b border-[#EDEDED] px-4 py-5 text-center"
                      >
                        <TermAccessCheckbox
                          checked={term.is_allowed}
                          onChange={(checked) =>
                            handleTermToggle(index, termName, checked)
                          }
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="flex justify-end gap-4 border-t border-gray-200 p-4">
        <Button
          type="button"
          variant="light"
          size="md"
          className="h-[48px] min-w-[120px] border border-[#5C88C5] bg-white text-[#1F1F1F]"
          onClick={onClose}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          size="md"
          className="h-[48px] min-w-[120px] bg-[#5C88C5] text-white"
          onClick={handleUpdate}
          disabled={loading || saving || courses.length === 0}
        >
          {saving ? "Updating..." : "Update"}
        </Button>
      </div>
    </Modal>
  );
}
